import { useState, useEffect, useRef, useCallback } from 'react';
import { Box, Skeleton, Group, Text, UnstyledButton, Stack, Loader, Button } from '@mantine/core';
import { IconRefresh } from '@tabler/icons-react';
import { RecipeCard } from '../MealPlanning/RecipeCard';
import { AuthButton } from '../AuthButton';
import { Recipe, MealPlan, MealPlanAssignment, Alternative } from '../../types/mealPlan';

interface HeroRecipeShowcaseProps {
  onCtaClick: () => void;
}

// Balanced across the audiences the landing page speaks to: allergen
// (Gluten-Free, Dairy-Free), lifestyle (Vegetarian, Vegan), diet programs
// (Keto), and nutrition goals (High-Protein, Low-Calorie, Low-Sodium).
// Low-Sodium also makes the campaign pages' "gluten-free AND low-sodium"
// promise demonstrable right in the demo. Every chip here must be in the
// backend's SAMPLE_RECIPE_CATEGORIES whitelist, have real catalog coverage,
// and be combinable with every other chip (no in-demo dead-end pairs — which
// is why Low-Fat, exclusive with Keto in the product, is absent).
// Ordering is layout-aware, not semantic: the row lives in a 400px box and
// must pack into TWO centered lines, so the short chips (Keto, Vegan) are
// placed to fill out lines the long labels leave ragged. Reordering
// alphabetically or by theme re-introduces a stray third line.
const DIET_OPTIONS = [
  'High-Protein',
  'Low-Calorie',
  'Low-Sodium',
  'Keto',
  'Gluten-Free',
  'Vegetarian',
  'Vegan',
  'Dairy-Free',
];

// Gluten-Free already carries the allergen story in the tighter mobile row
const MOBILE_HIDDEN_DIETS = ['Dairy-Free'];

const MAX_SELECTIONS = DIET_OPTIONS.length;

// Get consecutive day names starting from today (e.g., ["Wednesday", "Thursday", "Friday"])
const getDayNames = (count: number): string[] => {
  const today = new Date();
  return Array.from({ length: count }, (_, i) => {
    const date = new Date(today);
    date.setDate(today.getDate() + i);
    return date.toLocaleDateString('en-US', { weekday: 'long' });
  });
};

// Get today's date in ISO format (YYYY-MM-DD)
const getTodayDate = (): string => {
  return new Date().toISOString().split('T')[0];
};

// CSS keyframes for fade-in animation
const fadeInStyles = `
  @keyframes heroFadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

export const HeroRecipeShowcase: React.FC<HeroRecipeShowcaseProps> = ({ onCtaClick }) => {
  const [recipes, setRecipes] = useState<Recipe[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedDiets, setSelectedDiets] = useState<string[]>([]);
  const [transitioning, setTransitioning] = useState(false);
  const [showLoader, setShowLoader] = useState(false);
  // A narrow diet combination can have only the three recipes on screen behind
  // it, in which case re-rolling returns the same three and the control is
  // dead. Mirrors the picker, which retires "Fresh ideas" on a drained pool.
  const [exhausted, setExhausted] = useState(false);
  const hasFetched = useRef(false);
  const requestIdRef = useRef(0);
  const loaderTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const preloadImage = (url: string): Promise<void> => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve();
      img.onerror = () => resolve();
      img.src = url;
    });
  };

  const fetchRecipes = useCallback(async (diets: string[]) => {
    try {
      const params = diets.length > 0 ? `?diets=${diets.join(',')}` : '';
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/sample-recipes${params}`);
      if (!response.ok) {
        throw new Error('Failed to fetch sample recipes');
      }
      const data = await response.json();
      const fetchedRecipes = data.recipes || [];

      const imageUrls = fetchedRecipes
        .map((r: Recipe) => r.imageUrl)
        .filter((url: string | undefined): url is string => !!url);

      await Promise.all(imageUrls.map(preloadImage));

      return fetchedRecipes;
    } catch (err) {
      console.error('Error fetching sample recipes:', err);
      throw err;
    }
  }, []);

  // Initial fetch
  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;

    const load = async () => {
      try {
        const fetchedRecipes = await fetchRecipes([]);
        setRecipes(fetchedRecipes);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unknown error');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [fetchRecipes]);

  // Clean up a pending loader timer if the component unmounts mid-fetch
  useEffect(() => {
    return () => {
      if (loaderTimeoutRef.current) clearTimeout(loaderTimeoutRef.current);
    };
  }, []);

  // Shared by the diet chips and "Fresh ideas": both swap the demo's recipes
  // with the same cross-fade, deferred loader and stale-response guard.
  const loadRecipes = useCallback(
    async (diets: string[], onLoaded?: (fresh: Recipe[]) => void) => {
      // Tag this request so a slower, superseded fetch can't overwrite the
      // recipes for a newer selection.
      const requestId = ++requestIdRef.current;
      setTransitioning(true);

      // Only surface a spinner if the load is slow enough to notice, so brief
      // fetches just cross-fade instead of flashing a loader on and off.
      if (loaderTimeoutRef.current) clearTimeout(loaderTimeoutRef.current);
      loaderTimeoutRef.current = setTimeout(() => {
        if (requestId === requestIdRef.current) setShowLoader(true);
      }, 180);

      try {
        const fetchedRecipes = await fetchRecipes(diets);
        if (requestId !== requestIdRef.current) return; // A newer selection superseded this one
        setRecipes(fetchedRecipes);
        onLoaded?.(fetchedRecipes);
      } catch {
        // Keep existing recipes on error
      } finally {
        if (requestId === requestIdRef.current) {
          if (loaderTimeoutRef.current) clearTimeout(loaderTimeoutRef.current);
          setTransitioning(false);
          setShowLoader(false);
        }
      }
    },
    [fetchRecipes]
  );

  const handleDietToggle = async (diet: string) => {
    const newDiets = selectedDiets.includes(diet)
      ? selectedDiets.filter((d) => d !== diet)
      : selectedDiets.length >= MAX_SELECTIONS
        ? selectedDiets
        : [...selectedDiets, diet];

    if (newDiets.length === selectedDiets.length && newDiets.every((d) => selectedDiets.includes(d))) {
      return; // No change
    }

    setSelectedDiets(newDiets);
    // A different selection is a different pool, so give the button back.
    setExhausted(false);
    await loadRecipes(newDiets);
  };

  // Re-rolls the demo against the same diet selection. The endpoint shuffles
  // the whole eligible set on every call, so this needs nothing new from the
  // server — it just asks again.
  const handleFreshIdeas = async () => {
    const shownIds = recipes.map((r) => r.id).join(',');
    await loadRecipes(selectedDiets, (fresh) => {
      if (fresh.map((r) => r.id).join(',') === shownIds) setExhausted(true);
    });
  };

  const dietChips = (
    <Box>
      <Group gap={6} wrap="wrap" justify="center">
        {DIET_OPTIONS.map((diet) => {
          const isSelected = selectedDiets.includes(diet);
          const isDisabled = !isSelected && selectedDiets.length >= MAX_SELECTIONS;
          const isMobileHidden = MOBILE_HIDDEN_DIETS.includes(diet);
          return (
            <UnstyledButton
              key={diet}
              onClick={() => handleDietToggle(diet)}
              disabled={isDisabled}
              {...(isMobileHidden ? { visibleFrom: 'sm' as const } : {})}
              style={{
                padding: '4px 10px',
                borderRadius: 'var(--mantine-radius-xl)',
                fontSize: '13px',
                fontWeight: 500,
                border: isSelected
                  ? '1px solid var(--mantine-color-orange-4)'
                  : '1px solid var(--mantine-color-gray-3)',
                background: isSelected
                  ? 'var(--mantine-color-orange-0)'
                  : 'white',
                color: isSelected
                  ? 'var(--mantine-color-orange-7)'
                  : isDisabled
                    ? 'var(--mantine-color-gray-4)'
                    : 'var(--mantine-color-gray-7)',
                cursor: isDisabled ? 'default' : 'pointer',
                transition: 'all 0.15s ease',
              }}
            >
              {diet}
            </UnstyledButton>
          );
        })}
      </Group>
    </Box>
  );

  if (loading) {
    return (
      <Box maw={400} w="100%">
        {dietChips}
        <Box mt="md" style={{ position: 'relative', paddingBottom: 20 }}>
          <Skeleton height={480} radius="lg" />
          <Box
            style={{
              position: 'absolute',
              bottom: 0,
              left: '10%',
              right: '10%',
              height: 40,
              background: 'radial-gradient(ellipse at center, rgba(0, 0, 0, 0.08) 0%, transparent 70%)',
              borderRadius: '50%',
              zIndex: -1,
            }}
          />
        </Box>
      </Box>
    );
  }

  if (error || recipes.length === 0) {
    return (
      <>
        <style>{fadeInStyles}</style>
        <Box maw={400} w="100%">
          {dietChips}
          {selectedDiets.length > 0 && !error ? (
            <Box
              mt="md"
              p="xl"
              mih={300}
              style={{
                borderRadius: 'var(--mantine-radius-lg)',
                border: '1px solid var(--mantine-color-gray-2)',
                background: 'var(--mantine-color-gray-0)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                animation: 'heroFadeIn 0.3s ease-out forwards',
              }}
            >
              <Stack gap="md" align="center" ta="center">
                <Text size="lg" fw={600} c="dark">
                  {selectedDiets.join(' + ')}? We've got you.
                </Text>
                <Text size="sm" c="dimmed">
                  This combination needs custom recipes — sign up and we'll build a plan tailored to all of your needs.
                </Text>
                <AuthButton size="md">
                  Get custom recipes
                </AuthButton>
              </Stack>
            </Box>
          ) : (
            <Box
              mt="md"
              component="img"
              src="/images/recipe-example-1.png"
              alt="Meal planning example"
              style={{
                maxWidth: 400,
                width: '100%',
                borderRadius: 'var(--mantine-radius-lg)',
                boxShadow: 'var(--mantine-shadow-xl), 0 8px 16px -4px rgba(0, 0, 0, 0.1)',
                animation: 'heroFadeIn 0.5s ease-out forwards',
              }}
            />
          )}
        </Box>
      </>
    );
  }

  // Build mock assignment and meal plan for the RecipeCard
  const demoDays = getDayNames(recipes.length);
  const todayDay = demoDays[0];
  const todayDate = getTodayDate();

  const selectedRecipe = recipes[0];
  const alternatives: Alternative[] = recipes.slice(1).map((recipe) => ({
    recipe,
    reasoning: '',
    source: 'ai_generated' as const,
  }));

  const alternativesOrder = recipes.map((r) => r.id);

  const mockAssignment: MealPlanAssignment = {
    id: 'demo-assignment',
    plannedDay: todayDay,
    plannedDate: todayDate,
    servings: selectedRecipe.servings,
    scaledRecipe: selectedRecipe,
    reasoning: '',
    source: 'ai_generated',
    alternatives,
    alternativesOrder,
  };

  const mockMealPlan: MealPlan = {
    id: 'demo-meal-plan',
    userId: 'demo-user',
    startDate: todayDate,
    endDate: todayDate,
    createdAt: todayDate,
    assignments: [mockAssignment],
    preferences: {
      recipeSources: 'ai_only',
    },
    status: 'draft',
    generationStatus: { status: 'completed' },
    aiSubstitutionsUsed: 0,
    maxAiSubstitutions: 0,
  };

  const noOp = () => {};
  const noOpAsync = async () => {};
  const noOpSet = <T,>() => (() => {}) as React.Dispatch<React.SetStateAction<T>>;

  return (
    <>
      <style>{fadeInStyles}</style>
      <Box maw={400} w="100%">
        {dietChips}
        <Box
          mt="md"
          style={{
            animation: 'heroFadeIn 0.5s ease-out forwards',
            paddingBottom: 20,
          }}
        >
          <Box style={{ position: 'relative' }}>
            <Box
              style={{
                position: 'absolute',
                bottom: -20,
                left: '10%',
                right: '10%',
                height: 40,
                background: 'radial-gradient(ellipse at center, rgba(0, 0, 0, 0.08) 0%, transparent 70%)',
                borderRadius: '50%',
                zIndex: -1,
              }}
            />
          <Box
            style={{
              opacity: transitioning ? 0.5 : 1,
              // Snap to dim instantly on the way in for immediate feedback;
              // only ease back to full opacity when the new recipes arrive.
              transition: transitioning ? 'none' : 'opacity 0.2s ease',
            }}
          >
          <RecipeCard
            assignment={mockAssignment}
            isOwner={false}
            mealPlan={mockMealPlan}
            loadingActivation={false}
            menuOpen={{}}
            openTooltip={null}
            servingsInputMode={null}
            tempServings={0}
            savingDays={new Set()}
            substitutingDays={new Set()}
            swappingDays={new Set()}
            ratingDays={new Set()}
            handleAssignmentClick={noOp}
            handleToggleSaveRecipe={noOp}
            handleTooltipClick={noOp}
            handleSubstituteWithInstructions={noOp}
            handleServingsInputMode={noOp}
            handleSubmitServings={noOp}
            handleAssignmentFeedback={noOp}
            handleRateUp={noOp}
            handleRateDown={noOp}
            handleUndoRating={noOp}
            handleDeleteAssignment={noOp}
            handleSwapAlternative={noOpAsync}
            setMenuOpen={noOpSet()}
            setServingsInputMode={noOpSet()}
            setTempServings={noOpSet()}
            setSelectedRecipeForNutrition={noOpSet()}
            setNutritionDrawerOpened={noOpSet()}
            demoMode={true}
            demoDays={demoDays}
            onDemoCtaClick={onCtaClick}
            userDietaryRestrictions={selectedDiets}
            radius="lg"
          />
          </Box>
          {transitioning && (
            <Box
              style={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 5,
                // Transparent shield: capturing pointer events here stops the
                // carousel underneath from being dragged or its dots clicked
                // while the next recipes load. It must sit above the card in
                // the stacking order — the carousel's own pointerEvents:auto
                // controls would override an ancestor's pointerEvents:none,
                // but can't override a sibling layered on top.
                pointerEvents: 'auto',
                cursor: 'default',
              }}
            >
              {showLoader && <Loader color="orange" size="md" />}
            </Box>
          )}
          </Box>
        </Box>
        {/* Deliberately quiet. The card's own "View full recipe" is a filled,
            full-width AuthButton and has to stay the loudest thing here — this
            is an invitation to keep looking, not a competing call to action.
            (The picker's copy of this button is the opposite: there, seeing
            different recipes IS the goal of the screen.)

            Sits outside the box above, whose drop shadow hangs 20px below the
            card. */}
        {!exhausted && (
          <Group justify="center" mt="xs">
            <Button
              variant="subtle"
              color="orange"
              size="sm"
              leftSection={<IconRefresh size={14} />}
              onClick={handleFreshIdeas}
              // Disabled, never spinning. The card already shows a loader over
              // itself while the demo swaps, and a second spinner on the
              // button is just noise on top of it. This matches how the
              // control behaves during a diet-chip load, which is the same
              // event from the visitor's point of view.
              disabled={transitioning}
            >
              Fresh ideas
            </Button>
          </Group>
        )}
      </Box>
    </>
  );
};
